import type {
  Condition,
  RulePayload,
  StringOp,
  TriggerKind,
} from "@workspace/contracts/embed"

import type { PageContext } from "./host"
import { read, write } from "./storage"

/**
 * A payload's rules, run: they arrive ENABLED and in priority order, and
 * the runtime takes the FIRST whose conditions fit this page, arms its
 * trigger, and fires once the trigger does. One rule at a time: the
 * conditions are decided at load (the path, the utm, the device, the
 * referrer do not change under a page), so the walk is a filter and the
 * winner is the first survivor. A rule marked `once` remembers it fired in
 * `sessionStorage` and steps aside for the visit; the next survivor arms
 * instead. An empty list arms nothing - the surface rests.
 */

const ONCE_KEY = (ruleId: string) => `usereq:once:${ruleId}`

function matchString(op: StringOp, value: string, expected: string): boolean {
  switch (op) {
    case "is":
      return value === expected
    case "starts_with":
      return value.startsWith(expected)
    case "contains":
      return value.includes(expected)
    case "matches":
      try {
        return new RegExp(expected).test(value)
      } catch {
        return false
      }
  }
}

export function conditionHolds(
  condition: Condition,
  page: PageContext
): boolean {
  switch (condition.kind) {
    case "path":
      // `"*"` is "any page" - the rule fires everywhere.
      return (
        condition.value === "*" ||
        matchString(condition.op, page.path, condition.value)
      )
    case "utm":
      return matchString(
        condition.op,
        page.utm[condition.param] ?? "",
        condition.value
      )
    case "device":
      return page.device === condition.value
    case "referrer":
      return matchString(condition.op, page.referrer, condition.value)
  }
}

/** The first rule whose every condition holds and that has not spent its `once`. */
export function firstMatch(
  rules: readonly RulePayload[],
  page: PageContext
): RulePayload | null {
  for (const rule of rules) {
    if (rule.once && read("sessionStorage", ONCE_KEY(rule.id))) continue
    if (rule.conditions.every((c) => conditionHolds(c, page))) return rule
  }
  return null
}

type Disposer = () => void

/**
 * Arm one trigger; `fire` runs once, then the listeners are gone. The
 * disposer tears the trigger down before it fires (the element left the
 * page).
 */
export function armTrigger(
  trigger: TriggerKind,
  params: Record<string, unknown>,
  fire: () => void
): Disposer {
  let done = false
  let cleanup: Disposer = () => {}
  const once = () => {
    if (done) return
    done = true
    cleanup()
    fire()
  }

  switch (trigger) {
    case "page_load": {
      const timer = window.setTimeout(once, 0)
      cleanup = () => window.clearTimeout(timer)
      break
    }
    case "time_on_page": {
      const seconds = Number(params.seconds) || 0
      const timer = window.setTimeout(once, seconds * 1000)
      cleanup = () => window.clearTimeout(timer)
      break
    }
    case "scroll_depth": {
      const percent = Number(params.percent) || 0
      const check = () => {
        const root = document.documentElement
        const scrollable = root.scrollHeight - window.innerHeight
        const depth =
          scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100
        if (depth >= percent) once()
      }
      window.addEventListener("scroll", check, { passive: true })
      cleanup = () => window.removeEventListener("scroll", check)
      check()
      break
    }
    case "element_click": {
      const selector = String(params.selector ?? "")
      const onClick = (event: Event) => {
        const target = event.target
        if (!(target instanceof Element) || !selector) return
        try {
          if (target.closest(selector)) once()
        } catch {
          // A selector the browser cannot parse never matches.
        }
      }
      document.addEventListener("click", onClick, true)
      cleanup = () => document.removeEventListener("click", onClick, true)
      break
    }
    case "element_visible": {
      const selector = String(params.selector ?? "")
      let observer: IntersectionObserver | null = null
      const watch = () => {
        let element: Element | null = null
        try {
          element = selector ? document.querySelector(selector) : null
        } catch {
          element = null
        }
        if (!element) return false
        observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) once()
        })
        observer.observe(element)
        return true
      }
      // The element may render after the tag: look again when the DOM is done.
      if (!watch() && document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => void watch(), {
          once: true,
        })
      }
      cleanup = () => observer?.disconnect()
      break
    }
    case "exit_intent": {
      // The pointer leaves through the top edge - toward the tabs, the bar.
      const onOut = (event: MouseEvent) => {
        if (event.relatedTarget === null && event.clientY <= 0) once()
      }
      document.addEventListener("mouseout", onOut)
      cleanup = () => document.removeEventListener("mouseout", onOut)
      break
    }
  }

  return () => {
    done = true
    cleanup()
  }
}

/**
 * The whole walk: pick the first fitting rule, arm it, hand the rule over
 * when it fires. Spends the rule's `once` on firing.
 */
export function runRules(
  rules: readonly RulePayload[],
  page: PageContext,
  onFire: (rule: RulePayload) => void
): Disposer {
  const rule = firstMatch(rules, page)
  if (!rule) return () => {}
  return armTrigger(rule.trigger, rule.triggerParams, () => {
    if (rule.once) write("sessionStorage", ONCE_KEY(rule.id), "1")
    onFire(rule)
  })
}
